import React, { useEffect, useMemo, useState } from 'react'
import { PaymentElement, useCheckout } from '@stripe/react-stripe-js'
import Modal from './Modals/Modal'
import Button from './Buttons'
import { LuLoaderCircle, LuLock } from 'react-icons/lu'
import { useAnnualPlan, usePricing } from '../hooks/featureFlags'
import { ThanksContent } from './Modals/Thanks'
import { useAuth } from '../contexts/Auth'

export const Return = ({ closeModal }: { closeModal: () => void }) => {
	const [status, setStatus] = useState<string | null>(null)
	const { currentUser } = useAuth() as any

	useEffect(() => {
		const fetchSessionStatus = async (sessionId: string) => {
			const requestOptions = {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
				},
				body: JSON.stringify({
					sessionId,
				}),
			}
			fetch(process['env']['REACT_APP_API_ROOT'] + '/payments/check-checkout-session/', requestOptions)
				.then((res) => res.json())
				.then((response: { status: string }) => {
					if (response.status) {
						setStatus(response.status)
					}
				})
				.catch((err) => {
					console.error(err)
				})
		}

		const queryString = window.location.search
		const urlParams = new URLSearchParams(queryString)
		const sessionId = urlParams.get('session_id')
		if (sessionId) {
			fetchSessionStatus(sessionId)
		}
	}, [])

	if (status === 'complete') {
		return (
			<Modal open={true} closeModal={closeModal} portalClassName="subscription v2">
				<ThanksContent setThanksModalOpen={() => closeModal()} />
			</Modal>
		)
	}

	return (
		<Modal open={true} closeModal={closeModal} portalClassName="subscription v2">
			<div className="flex items-center justify-center h-full min-h-[500px]">
				{LuLoaderCircle({ className: `animate-spin text-gray-500 w-8 h-8` })}
			</div>
		</Modal>
	)
}

const validateEmail = async (email: string, checkout: any) => {
	const updateResult = await checkout.updateEmail(email)
	const isValid = updateResult.type !== 'error'

	return { isValid, message: !isValid ? updateResult.error.message : null }
}

const EmailInput = ({
	email,
	setEmail,
	error,
	setError,
}: {
	email: string
	setEmail: (email: string) => void
	error: string | null
	setError: (error: string | null) => void
}) => {
	const checkout = useCheckout()

	const handleBlur = async () => {
		if (!email) {
			return
		}

		const { isValid, message } = await validateEmail(email, checkout)
		if (!isValid) {
			setError(message)
		}
	}

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setError(null)
		setEmail(e.target.value)
	}

	return (
		<>
			<label className="block mb-4">
				<h4>Email</h4>
				<input
					id="email"
					type="text"
					value={email}
					onChange={handleChange}
					onBlur={handleBlur}
					placeholder="you@example.com"
					className="w-full border border-gray-300 rounded-md p-2 shadow-sm"
				/>
			</label>
			{error && <div id="email-errors">{error}</div>}
		</>
	)
}

const CheckoutForm = ({ goBack, onClose }: { goBack: () => void; onClose: () => void }) => {
	const searchParamsString = document.location.search
	const checkout = useCheckout()

	const [email, setEmail] = useState('')
	const [emailError, setEmailError] = useState<string | null>(null)
	const [message, setMessage] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [annual] = useAnnualPlan()

	const pricing = usePricing()

	const searchParams = useMemo(() => {
		let params = new URLSearchParams(searchParamsString)

		return {
			checkoutComplete: params.getAll('checkout_complete')[0],
		}
	}, [searchParamsString])

	// @ts-ignore
	// checkout.on('change', (session: any) => {
	// 	// Handle changes to the checkout session
	// })

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()

		setIsLoading(true)

		const { isValid, message } = await validateEmail(email, checkout)
		if (!isValid) {
			setEmailError(message)
			setMessage(message)
			setIsLoading(false)
			return
		}

		const confirmResult = await checkout.confirm()

		// This point will only be reached if there is an immediate error when
		// confirming the payment. Otherwise, your customer will be redirected to
		// your `return_url`. For some payment methods like iDEAL, your customer will
		// be redirected to an intermediate site first to authorize the payment, then
		// redirected to the `return_url`.
		if (confirmResult.type === 'error') {
			setMessage(confirmResult.error.message)
		}

		setIsLoading(false)
	}

	if (searchParams.checkoutComplete) {
		return (
			<Modal open={true} closeModal={onClose} portalClassName="subscription v2">
				<Return closeModal={onClose} />
			</Modal>
		)
	}

	return (
		<Modal open={true} closeModal={onClose} portalClassName="subscription v2">
			<div className="text-2xl font-bold m-4 pb-3 border-b border-gray-200">Payment Information</div>
			<div className="p-4 pt-2 text-sm text-gray-900 text-center font-medium">
				Includes benefits like: Unlimited Essays, Personalized Writing Style and AI Humanizer and Detector.
			</div>
			<form onSubmit={handleSubmit} className=" p-4 pt-1">
				<EmailInput email={email} setEmail={setEmail} error={emailError} setError={setEmailError} />
				{/* <h4>Billing Address</h4> */}
				{/* <AddressElement options={{ mode: 'billing' }} /> */}
				<h4 className="mb-1">Payment</h4>
				<PaymentElement
					id="payment-element"
					options={{ fields: { billingDetails: { address: { country: 'auto' } } } }}
				/>
				<div className="flex justify-center items-center bg-link bg-opacity-15 p-3 rounded-md text-sm text-[#1966e3] font-medium mt-4">
					{LuLock({ className: 'mr-2', size: 18 })}
					<div className="">Your payment information is secured with industry-standard encryption</div>
				</div>
				<div className="flex justify-between gap-2 pt-4 pb-2">
					<div className="text-lg font-semibold">Total</div>
					<div className="text-xl font-semibold text-[#1966e3]">
						{pricing.currencySymbol}
						{annual ? pricing.yearlyTotal : pricing.monthly}
					</div>
				</div>
				<div className="pb-4 sticky bottom-0 left-0 right-0 bg-white flex flex-col gap-2">
					<div className="grid grid-cols-2 gap-4">
						<Button disabled={isLoading} id="submit" type="outline" className="mt-3" onClick={goBack}>
							Back
						</Button>
						<Button disabled={isLoading} id="submit" type="blue" className=" mt-3" htmlType="submit">
							{isLoading ? LuLoaderCircle({ className: 'animate-spin text-white w-5 h-5 inline-block' }) : `Pay now`}
						</Button>
					</div>
					{/* Show any error or success messages */}
					{message && (
						<div id="payment-message" className="text-sm text-center font-semibold">
							{message}
						</div>
					)}
				</div>
			</form>
		</Modal>
	)
}

export default CheckoutForm
